import React, { useEffect, useState, useMemo } from 'react';
import { AddressInputProps, Box } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { GetPlaceResponse } from '@wix/ambassador-wix-atlas-service-web/types';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams, ClearAll } from '@utils/route';
import { generateLocationLabel } from '@utils/locationLabel';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import ExploreAddressInput from '../../Filters/PartnerDetails/ExploreAddressInput';

interface MobileLanguageFilterProps {
  onClose: () => void;
  onBack: () => void;
  closeModal: () => void;
  clearWasClicked?: ClearAll;
  setClearWasClicked?: (x: ClearAll) => void;
}

export const mobileLocationFilterDataHook = 'mobile-location-filter';

const MobileLanguageFilter = ({
  onClose,
  onBack,
  closeModal,
  clearWasClicked,
  setClearWasClicked,
}: MobileLanguageFilterProps) => {
  const { sessionId } = useBiSessionContext();
  const {
    state: { selectedLocation, entry, origin, currentPage },
  } = useExploreContext();
  const { t } = useTranslation();
  const logger = useBi();
  const { navigate, router } = useShallowRouter();

  const hasBeenCleared = useMemo(() => {
    return clearWasClicked?.all || clearWasClicked?.location;
  }, [clearWasClicked?.all, clearWasClicked?.location]);

  const [location, setLocation] = useState<GetPlaceResponse['place'] | null>();
  const [locationInput, setLocationInput] = useState('');
  const [locationStatus, setLocationStatus] =
    useState<AddressInputProps['status']>();
  useEffect(() => {
    if (selectedLocation && !hasBeenCleared) {
      setLocation(selectedLocation);
      setLocationInput(
        generateLocationLabel(selectedLocation?.address ?? {}) ?? '',
      );
      setLocationStatus(undefined);
    } else {
      setLocation(null);
      setLocationInput('');
    }
  }, [selectedLocation, hasBeenCleared]);

  const onClickApply = () => {
    const hasClearedLocationFilter = !locationInput;
    const selectedLocation = hasClearedLocationFilter
      ? ''
      : location?.placeId ?? '';

    navigate(
      updateQueryParams(
        {
          location: selectedLocation,
        },
        router,
        clearWasClicked,
      ),
    );
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'apply partner lng/location filter',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          location: location?.address?.countryFullname,
          languageSelections: '',
          currentPage,
        }),
      }),
    );
    closeModal();
  };

  const onClearLocation = () => {
    setLocationInput('');
  };

  const onChangeLocation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocationInput(event.target.value);
  };

  const onSelectLocation = (selectedLocation: GetPlaceResponse['place']) => {
    setLocation(selectedLocation);
    const newLocationLabel = selectedLocation?.address
      ? generateLocationLabel(selectedLocation.address)
      : '';
    setLocationInput(newLocationLabel ?? '');
  };

  const onClickClear = () => {
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'clear all location filters',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          currentPage,
        }),
      }),
    );
    setClearWasClicked &&
      setClearWasClicked({ ...clearWasClicked!, location: true });
    setLocationInput('');
    setLocation(null);
  };

  return (
    <MobileFilterModalLayout
      title={t('mobile-filter-location')}
      dataHook={mobileLocationFilterDataHook}
      selectedAmount={location && locationInput !== '' ? 1 : 0}
      content={
        <Box
          backgroundColor="white"
          direction="vertical"
          marginLeft="24px"
          marginRight="24px"
          marginTop="12px"
        >
          <ExploreAddressInput
            autoFocus
            value={locationInput}
            onSelectPlace={onSelectLocation}
            onClear={onClearLocation}
            onInputChange={onChangeLocation}
            statusProp={locationStatus}
            size="large"
          />
        </Box>
      }
      onApply={onClickApply}
      onClose={onClose}
      onBack={onBack}
      onClear={onClickClear}
    />
  );
};

export default MobileLanguageFilter;
